// components/ServiceRoute.jsx
// ─────────────────────────────────────────────────────────────
// Route-level service guard. Wrap any group of routes that
// require a particular service to be enabled.
// If the service is off, renders a full-page block instead of
// the page — even if the user typed the URL directly.
//
// Usage in routes.jsx:
//   { element: <ServiceRoute serviceKey="trading" />, children: [
//     { path: "/buy",  element: <BuyCrypto /> },
//     { path: "/sell", element: <SellCrypto /> },
//   ]}
// ─────────────────────────────────────────────────────────────

import { Outlet, useNavigate } from "react-router-dom";
import { useServices } from "../hooks/useServices";

const SERVICE_ICONS = {
  trading:          "ri-exchange-dollar-line",
  deposits:         "ri-arrow-down-circle-line",
  withdrawals:      "ri-arrow-up-circle-line",
  naira_withdrawal: "ri-bank-line",
  bills:            "ri-receipt-line",
  fiat_deposit:     "ri-money-dollar-circle-line",
  rewards:          "ri-gift-line",
  kyc:              "ri-shield-user-line",
  support_chat:     "ri-customer-service-2-line",
  internal_transfer: "ri-bank-line",
  savings: "ri-arrow-down-circle-line",
  payment_link: "ri-link",
  payroll: "ri-team-line"
};

const SERVICE_NAMES = {
  trading:          "Buy & Sell Crypto",
  deposits:         "Crypto Deposits",
  withdrawals:      "Crypto Withdrawals",
  naira_withdrawal: "Naira Withdrawal",
  bills:            "Bill Payments",
  fiat_deposit:     "Naira Deposit",
  rewards:          "Rewards",
  kyc:              "KYC Verification",
  support_chat:     "Live Support",
  internal_transfer:"Internal Transfer",
  savings:          "Savings",
  payment_link:     "Payment Link",
  payroll:          "Payroll",
};

export default function ServiceRoute({ serviceKey }) {
  const navigate = useNavigate();
  const { services, isEnabled, getMessage, loading } = useServices();

  // While service flags are loading, render nothing to avoid a flash
  // of blocked content before the status is known.
  if (loading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="w-7 h-7 border-2 border-t-brand rounded-full animate-spin" style={{ borderColor: "var(--color-brand-light)", borderTopColor: "var(--color-brand)" }} />
      </div>
    );
  }

  // Service is enabled (or unknown key) → render the page normally
  if (isEnabled(serviceKey)) return <Outlet />;

  // Service is disabled → full-page block
  const icon    = services[serviceKey]?.icon || SERVICE_ICONS[serviceKey] || "ri-tools-line";
  const name    = services[serviceKey]?.name || SERVICE_NAMES[serviceKey] || serviceKey;
  const message = getMessage(serviceKey);

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      {/* Minimal back header */}
      <div className="bg-surface px-5 pt-12 pb-4 border-b border-border">
        <button onClick={() => navigate("/home")}
          className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center hover:bg-surface-hover transition-colors">
          <i className="ri-arrow-left-line text-secondary text-lg" />
        </button>
      </div>

      {/* Blocked content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
        <div className="w-20 h-20 rounded-3xl bg-surface-hover flex items-center justify-center mx-auto mb-5">
          <i className={`${icon} text-faint text-4xl`} />
        </div>
        <h2 className="text-primary font-bold text-xl mb-2 font-display">
          {name} Unavailable
        </h2>
        <p className="text-muted text-sm leading-relaxed mb-8 max-w-xs">{message}</p>
        <button onClick={() => navigate("/home")}
          className="px-8 py-3 bg-brand hover:bg-brand-hover text-on-brand font-semibold rounded-2xl text-sm transition-colors">
          Back to Home
        </button>
      </div>
    </div>
  );
}
